import apiClient from '../apiClient';

/**
 * Sales Order API — wraps existing backend endpoints for the
 * salesperson and warehouse order flow.
 */

const transformOrder = (order) => {
    if (!order) return null;
    const customer = order.customer || {};
    return {
        ...order,
        id: order.publicId || order.id,
        orderRef: order.orderRefNo ? `#${order.orderRefNo}` : `#${(order.publicId || order.id || '').slice(-8)}`,
        customer: {
            ...customer,
            name: customer.name || 'Unknown Customer',
            phone: customer.mobileNumber || customer.whatsappNumber || '',
            email: customer.email || '',
            address: customer.address || customer.deliveryAddress || '',
        },
        salesman: order.assignedSalesPersonName || 'Unassigned',
        orderSalesman: order.orderSalesPersonName,
        customerSalesman: order.customerSalesPersonName,
        delegatedSalesman: order.delegatedSalesPersonName,
        deliverySequence: order.deliverySequence ?? null,
        items: (order.items || []).map(item => ({
            ...item,
            id: item.productPublicId || item.publicId || item.id,
            name: item.productName || item.name || 'Product',
            quantity: item.quantity || 0,
            unit: item.unit || item.unitOfMeasure || 'pcs',
            price: item.price || item.unitPrice || 0,
            total: item.subtotal || item.total || item.lineTotal || (item.quantity * (item.price || item.unitPrice || 0))
        }))
    };
};

/**
 * Get ALL orders for the tenant (authenticated endpoint).
 * Used by Supplier / Admin roles that see all orders.
 */
export const getAllOrders = async (params = {}) => {
    try {
        const { page = 0, size = 10, sort = 'createdAt,desc' } = params;
        const response = await apiClient.get('/orders', {
            params: { page, size, sort }
        });
        const data = response.data;
        const orders = Array.isArray(data) ? data : (data.content || []);
        return {
            orders: orders.map(transformOrder),
            totalElements: data.totalElements || orders.length,
            totalPages: data.totalPages || 1,
            currentPage: data.number || page
        };
    } catch (error) {
        console.error('Error fetching all orders:', error);
        throw error;
    }
};

/**
 * Get all active (non-terminal) orders for the current user/tenant.
 * Backend manages role-based access and handles what constitutes "active".
 */
export const getActiveOrders = async (params = {}) => {
    try {
        const { page = 0, size = 10, sort = 'createdAt,desc' } = params;
        const response = await apiClient.get('/orders/active', {
            params: { page, size, sort }
        });
        const data = response.data;
        const orders = Array.isArray(data) ? data : (data.content || []);
        return {
            orders: orders.map(transformOrder),
            totalElements: data.totalElements || orders.length,
            totalPages: data.totalPages || 1,
            currentPage: data.number || page
        };
    } catch (error) {
        console.error('Error fetching active orders:', error);
        throw error;
    }
};

/**
 * Search orders with filters (authenticated endpoint).
 * Backend uses Spring Pageable — page/size/sort go as query params.
 * POST body should only contain recognized search fields.
 */
export const searchOrders = async (filters = {}, params = {}) => {
    try {
        const { page = 0, size = 10, sort = 'createdAt,desc' } = params;
        // Only send recognized fields in the body
        const body = {};
        if (filters.salesPersonPublicId) body.salesPersonPublicId = filters.salesPersonPublicId;
        if (filters.statusPublicId) body.statusPublicId = filters.statusPublicId;
        if (filters.statusCodes && filters.statusCodes.length > 0) body.statusCodes = filters.statusCodes;
        if (filters.customerPublicId) body.customerPublicId = filters.customerPublicId;
        if (filters.productPublicId) body.productPublicId = filters.productPublicId;
        if (filters.searchTerm) body.searchTerm = filters.searchTerm;
        if (filters.startDate) body.startDate = filters.startDate;
        if (filters.endDate) body.endDate = filters.endDate;

        const response = await apiClient.post('/orders/search', body, {
            params: { page, size, sort }
        });
        const data = response.data;
        const orders = Array.isArray(data) ? data : (data.content || []);
        return {
            orders: orders.map(transformOrder),
            totalElements: data.totalElements || orders.length,
            totalPages: data.totalPages || 1,
            currentPage: data.number || page
        };
    } catch (error) {
        console.error('Error searching orders:', error);
        throw error;
    }
};

/**
 * Execute a workflow transition on an order.
 */
export const executeTransition = async (orderPublicId, transitionPublicId, payload = {}) => {
    try {
        const response = await apiClient.post(
            `/orders/${orderPublicId}/transitions/${transitionPublicId}`,
            payload
        );
        return transformOrder(response.data);
    } catch (error) {
        console.error('Error executing transition:', error);
        throw error;
    }
};

/**
 * Fetch workflow definition for a subscription plan.
 */
export const getWorkflowByPlan = async (subscriptionPlan) => {
    try {
        if (!subscriptionPlan) return null;
        const response = await apiClient.get(`/workflows/${subscriptionPlan.toUpperCase()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching workflow:', error);
        throw error;
    }
};

/**
 * Execute a workflow transition on multiple orders.
 */
export const executeBulkTransition = async (orderPublicIds, transitionPublicId, payload = {}) => {
    try {
        const response = await apiClient.post('/orders/bulk-transition', {
            orderPublicIds,
            transitionPublicId,
            ...payload
        });
        const data = response.data;
        return Array.isArray(data) ? data.map(transformOrder) : [];
    } catch (error) {
        console.error('Error executing bulk transition:', error);
        throw error;
    }
};

/**
 * Get the status history (audit trail) for an order.
 */
export const getHistory = async (orderPublicId) => {
    try {
        const response = await apiClient.get(`/orders/${orderPublicId}/history`);
        return response.data || [];
    } catch (error) {
        console.error('Error fetching order history:', error);
        throw error;
    }
};

/**
 * Place a new order (public endpoint — same as customer flow).
 */
export const placeOrder = async (orderData) => {
    try {
        const response = await apiClient.post('/public/orders', orderData);
        return response.data;
    } catch (error) {
        console.error('Error placing order:', error);
        throw error;
    }
};

/**
 * Batch confirm availability for a specific product across all pending orders.
 */
export const batchConfirmProduct = async (productPublicId, notes = '') => {
    try {
        const response = await apiClient.post('/orders/batch-confirm-product', {
            productPublicId,
            notes
        });
        return response.data;
    } catch (error) {
        console.error('Error batch confirming product:', error);
        throw error;
    }
};

/**
 * Get order counts per status code for the current user.
 * Returns an object: { [statusCode]: count }
 * One lightweight call used to populate tab badges.
 */
export const getOrderTabCounts = async (params = {}) => {
    try {
        const response = await apiClient.get('/orders/tab-counts', { params });
        return response.data || {};
    } catch (error) {
        console.error('Error fetching tab counts:', error);
        return {};
    }
};
/**
 * Re-assign an order to a different sales person.
 */
export const reassignOrder = async (orderPublicId, data) => {
    try {
        const response = await apiClient.put(`/orders/${orderPublicId}/reassign`, data);
        return transformOrder(response.data);
    } catch (error) {
        console.error('Error re-assigning order:', error);
        throw error;
    }
};

/**
 * Update an existing order's items, quantities, delivery date and time slot.
 * Only the assigned sales person or a manager/admin may call this.
 *
 * @param {string} orderPublicId
 * @param {{ items: Array<{productPublicId: string, quantity: number}>, deliveryDate?: string, timeSlot?: string }} payload
 */
export const updateOrder = async (orderPublicId, payload) => {
    try {
        const response = await apiClient.put(`/orders/${orderPublicId}`, payload);
        return transformOrder(response.data);
    } catch (error) {
        console.error('Error updating order:', error);
        throw error;
    }
};

/**
 * Download the PDF invoice for an order.
 */
export const downloadOrderPdf = async (orderPublicId) => {
    try {
        const response = await apiClient.get(`/orders/${orderPublicId}/pdf`, {
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoice_${orderPublicId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (error) {
        console.error('Error downloading order PDF:', error);
        throw error;
    }
};

/**
 * Download the PDF picking list for a salesperson.
 */
export const downloadPickingListPdf = async (salesPersonPublicId, salesPersonName, groupBySalesperson = false) => {
    try {
        const response = await apiClient.get('/orders/warehouse/picking-list/pdf', {
            params: { salesPersonPublicId, salesPersonName, groupBySalesperson },
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename = salesPersonName ? `PickingList_${salesPersonName.replace(/ /g, '_')}.pdf` : (groupBySalesperson ? 'PickingList_SalespersonWise.pdf' : 'PickingList_All.pdf');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (error) {
        console.error('Error downloading picking list PDF:', error);
        throw error;
    }
};

// ─── Route Sequencing ──────────────────────────────────────────────────────────

/**
 * Get the persistent route sequence for a sales person.
 * Returns an ordered list of { customerPublicId, customerName, customerAddress, sequenceNumber, ... }.
 * If salesPersonPublicId is omitted, defaults to the authenticated user.
 */
export const getRouteSequence = async (salesPersonPublicId = null) => {
    try {
        const params = salesPersonPublicId ? { salesPersonPublicId } : {};
        const response = await apiClient.get('/route-sequence', { params });
        return response.data || [];
    } catch (error) {
        console.error('Error fetching route sequence:', error);
        return [];
    }
};

/**
 * Update today's delivery sequence on a batch of orders.
 * sequences: [{ orderPublicId, sequenceNumber }]
 */
export const updateDeliverySequences = async (sequences) => {
    try {
        await apiClient.patch('/orders/delivery-sequence', { sequences });
    } catch (error) {
        console.error('Error updating delivery sequences:', error);
        throw error;
    }
};

/**
 * Upsert the persistent route sequence for a sales person.
 * sequences: [{ customerPublicId, sequenceNumber }]
 * If salesPersonPublicId is omitted, defaults to the authenticated user.
 */
export const upsertRouteSequence = async (sequences, salesPersonPublicId = null) => {
    try {
        const body = { sequences };
        if (salesPersonPublicId) body.salesPersonPublicId = salesPersonPublicId;
        await apiClient.put('/route-sequence', body);
    } catch (error) {
        console.error('Error upserting route sequence:', error);
        throw error;
    }
};

